// src/hooks/api/useMembers.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serverRequest } from '../../util/server_request';
import { notifyUser } from '../../util/util';

// Query Keys
export const membersKeys = {
  all: ['members'],
  lists: () => [...membersKeys.all, 'list'],
  list: (page, size) => [...membersKeys.lists(), { page, size }],
  detail: (id) => [...membersKeys.all, 'detail', id],
  search: (query) => [...membersKeys.all, 'search', query],
};

// Fetch all members with pagination
export const useMembers = (page = 0, size = 10) => {
  return useQuery({
    queryKey: membersKeys.list(page, size),
    queryFn: async () => {
      const result = await serverRequest('GET', `members?size=${size}&page=${page}`);
      return result.data || [];
    },
    keepPreviousData: true,
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Fetch all members (for dropdowns, etc.)
export const useAllMembers = () => {
  return useQuery({
    queryKey: [...membersKeys.all, 'all'],
    queryFn: async () => {
      let page = 0;
      const pageSize = 10000;
      let hasMore = true;
      const uniqueMembers = new Map();

      while (hasMore) {
        const result = await serverRequest('GET', `members?size=${pageSize}&page=${page}`);
        
        if (result.data && result.data.length > 0) {
          result.data.forEach(item => {
            const uniqueKey = `${item.ssnit_number}_${item.id || item.FullName || ''}`;
            
            if (!uniqueMembers.has(uniqueKey)) {
              uniqueMembers.set(uniqueKey, {
                label: item.FullName,
                value: item.ssnit_number,
                originalData: item,
                reactKey: uniqueKey
              });
            }
          });
          
          if (result.data.length < pageSize) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
        }
      }

      return Array.from(uniqueMembers.values());
    },
    staleTime: Infinity, // This data doesn't change often
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Fetch member details
export const useMember = (memberNo, enabled = true) => {
  return useQuery({
    queryKey: membersKeys.detail(memberNo),
    queryFn: async () => {
      const result = await serverRequest('GET', `members/${memberNo}`);
      return result.data;
    },
    enabled: enabled && !!memberNo,
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Create new member
export const useCreateMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberData) => {
      return await serverRequest('POST', 'members', memberData);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: membersKeys.all });
      notifyUser('success', 'Member created successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Update member
export const useUpdateMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, memberData }) => {
      return await serverRequest('PUT', `members/${id}`, memberData);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: membersKeys.all });
      queryClient.invalidateQueries({ queryKey: membersKeys.detail(variables.id) });
      notifyUser('success', 'Member updated successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Delete member
export const useDeleteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberId) => {
      return await serverRequest('DELETE', `members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersKeys.all });
      notifyUser('success', 'Member deleted successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};
